import type { Student } from '~entities/student';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Label } from '~shared/shadcn/ui/label';

import { formatStudentProfileAge } from '../lib/student-profile-age';

const FakeInput: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={cn(
      'flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-sm',
      className,
    )}
  >
    {children}
  </div>
);

export const StudentDetails = ({
  setIsEditing,
  data,
}: {
  data: Student;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { data: studioDetails } = useGetStudioDetails();
  const ageLabel = formatStudentProfileAge(data.dob, studioDetails?.timezone);

  return (
    <>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='space-y-2'>
          <Label htmlFor='firstName'>First Name:</Label>
          <FakeInput>{data.firstName}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='lastName'>Last Name:</Label>
          <FakeInput>{data.lastName}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='dob'>DOB:</Label>
          <FakeInput>{formatDateOnlySafe(data.dob, 'PPP')}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='age'>Age</Label>
          <FakeInput>{ageLabel}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='gender'>Gender:</Label>
          <FakeInput>{data.gender}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='tShirt'>Costume Size</Label>
          <FakeInput>{data.tShirtSize}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='mobile'>Mobile no</Label>
          <FakeInput>{data.mobileNo}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='email'>Email</Label>
          <FakeInput>{data.studentEmail}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='transportation'>Transportation</Label>
          <FakeInput>{data.transportation}</FakeInput>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='photo-opt-out'>Photo / video opt-out</Label>
          <FakeInput>{data.photoOptOut ? 'Yes — no photos/video' : 'No'}</FakeInput>
        </div>
      </div>
      <div className='space-y-4'>
        <h3 className='font-semibold'>Medical</h3>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='doctor'>Primary Doctor</Label>
            <FakeInput>{data.medical.primaryDoctor}</FakeInput>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='medication'>Medication</Label>
            <FakeInput>{data.medical.medications}</FakeInput>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='disabilities'>Disabilities</Label>
            <FakeInput>{data.medical.disabilities}</FakeInput>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='allergies'>Allergies</Label>
            <FakeInput>{data.medical.allergies}</FakeInput>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='special-needs'>Special Needs</Label>
            <FakeInput>{data.medical.specialNeeds}</FakeInput>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='insurance'>Health Insurance Carrier</Label>
            <FakeInput>{data.medical.healthInsuranceCarrier}</FakeInput>
          </div>
        </div>
      </div>{' '}
      <div className='flex flex-wrap justify-end gap-3'>
        <Button
          type='button'
          variant='secondary'
          onClick={() => {
            setIsEditing(true);
          }}
        >
          Edit
        </Button>
      </div>
    </>
  );
};
